import { BitkitPromoBanner, BitkitPromoBannerProps } from '@bitrise/bitkit-v2';

import { useAccountFeatureFlag } from '@/components/page/GlobalPropsContext';
import { useDismissableNotification } from '@/hooks/useDismissableNotification';

const LEARN_MORE_URL = 'https://docs.bitrise.io/en/bitrise-ci/run-and-analyze-builds/utility-builds';

// Exported for Storybook decorators that reset/set the dismissal cookie.
export const utilityBuildsBannerCookieName = (dismissKey: string) => `utility_builds_banner_dismissed_${dismissKey}`;

// `utilityBuildsEntitled` gates display only (it ignores the classification feature flag), so a workspace on a plan
// that includes utility builds is told about them even before any build is classified.
//
// Visibility lives in the hook (not the component) so pages can hide sibling layout elements (e.g. a Separator)
// together with the banner.
export const useUtilityBuildsBanner = (dismissKey: string) => {
  const isEntitled = useAccountFeatureFlag('utilityBuildsEntitled');
  const { isHidden, dismiss } = useDismissableNotification(utilityBuildsBannerCookieName(dismissKey));

  return { isVisible: isEntitled && !isHidden, onDismiss: dismiss };
};

type UtilityBuildsBannerProps = Omit<
  BitkitPromoBannerProps,
  'actionable' | 'dismissible' | 'message' | 'onClose' | 'title'
> & {
  onDismiss: () => void;
};

const UtilityBuildsBanner = ({ onDismiss, ...rest }: UtilityBuildsBannerProps) => {
  return (
    <BitkitPromoBanner
      title="Meet Utility builds - builds that don't count toward your plan"
      message="Some of your builds can now qualify as Utility builds. When a build meets the conditions, it doesn't use up one of your monthly builds."
      actionable={{ label: 'Learn more', href: LEARN_MORE_URL, target: '_blank' }}
      dismissible
      onClose={onDismiss}
      {...rest}
    />
  );
};

export default UtilityBuildsBanner;
